// client/src/components/common/ConfirmModal.jsx
import { useEffect, useCallback } from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle2, X } from 'lucide-react';

/**
 * Modal de Confirmación y Alerta Personalizado
 * Reemplaza los popups nativos del navegador ("localhost:3000 dice")
 * con una experiencia moderna, accesible y coherente con el sistema de diseño.
 */
export const ConfirmModal = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger', // 'danger' | 'warning' | 'info' | 'success'
  icon: CustomIcon,
  onConfirm,
  onCancel,
}) => {
  // Manejo de tecla ESC
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onCancel?.();
      }
    },
    [onCancel]
  );

  // Bloqueo de scroll en body al estar abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const variantConfig = {
    danger: {
      icon: CustomIcon || AlertTriangle,
      iconBg: 'bg-red-50 text-red-600 border-red-100',
      confirmBtn: 'bg-red-600 hover:bg-red-700 text-white shadow-xs focus:ring-red-500',
    },
    warning: {
      icon: CustomIcon || AlertCircle,
      iconBg: 'bg-amber-50 text-amber-600 border-amber-100',
      confirmBtn: 'bg-amber-600 hover:bg-amber-700 text-white shadow-xs focus:ring-amber-500',
    },
    info: {
      icon: CustomIcon || Info,
      iconBg: 'bg-blue-50 text-blue-600 border-blue-100',
      confirmBtn: 'bg-orange-600 hover:bg-orange-700 text-white shadow-xs focus:ring-orange-500',
    },
    success: {
      icon: CustomIcon || CheckCircle2,
      iconBg: 'bg-emerald-50 text-emerald-600 border-emerald-100',
      confirmBtn: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs focus:ring-emerald-500',
    },
  };

  const config = variantConfig[variant] || variantConfig.info;
  const IconComponent = config.icon;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-[9990] animate-in fade-in duration-150"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-desc"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border ${config.iconBg}`}
            >
              <IconComponent className="w-6 h-6 stroke-[2]" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3
                  id="confirm-dialog-title"
                  className="text-lg font-bold text-gray-900 leading-tight"
                >
                  {title || 'Confirmación requerida'}
                </h3>
                <button
                  type="button"
                  onClick={onCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {message && (
                <p
                  id="confirm-dialog-desc"
                  className="text-sm text-gray-600 mt-2 leading-relaxed"
                >
                  {message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          {cancelText && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
            >
              {cancelText}
            </button>
          )}

          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            className={`px-5 py-2 text-sm font-semibold rounded-xl transition-colors cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-offset-1 ${config.confirmBtn}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
